/**
 * Deterministic PRNG. The sim must never touch Math.random — replays, seeded
 * leaderboards and regression tests all depend on an identical stream.
 *
 * mulberry32: tiny, fast, good enough distribution for gameplay.
 */

export interface Rng {
  /** Mutable 32-bit state. Serialisable, so a world snapshot is reproducible. */
  s: number;
}

export function makeRng(seed: number): Rng {
  // Avoid a zero state, which degenerates.
  return { s: (seed >>> 0) || 0x9e3779b9 };
}

export function cloneRng(rng: Rng): Rng {
  return { s: rng.s };
}

/** Uniform float in [0, 1). */
export function rand(rng: Rng): number {
  rng.s = (rng.s + 0x6d2b79f5) >>> 0;
  let t = rng.s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Uniform float in [lo, hi). */
export function randRange(rng: Rng, lo: number, hi: number): number {
  return lo + rand(rng) * (hi - lo);
}

/** Uniform integer in [lo, hi] inclusive. */
export function randInt(rng: Rng, lo: number, hi: number): number {
  return lo + Math.floor(rand(rng) * (hi - lo + 1));
}

export function randSign(rng: Rng): number {
  return rand(rng) < 0.5 ? -1 : 1;
}

export function pick<T>(rng: Rng, items: readonly T[]): T {
  if (items.length === 0) throw new Error('pick from empty list');
  return items[randInt(rng, 0, items.length - 1)]!;
}

/**
 * A second, independent stream derived from a seed. Used by the renderer to
 * generate planet body art without disturbing the sim's stream.
 */
export function deriveSeed(seed: number, salt: number): number {
  let h = (seed ^ Math.imul(salt, 0x45d9f3b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Cheap deterministic hash of a number stream — used by determinism tests. */
export function hashNumbers(values: Iterable<number>): number {
  let h = 0x811c9dc5;
  for (const v of values) {
    // Quantise floats so tiny FP noise doesn't produce false negatives, while
    // still catching any real divergence.
    const q = Math.round(v * 1024) | 0;
    h = Math.imul(h ^ (q & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((q >>> 8) & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((q >>> 16) & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((q >>> 24) & 0xff), 0x01000193) >>> 0;
  }
  return h >>> 0;
}
